import React, { useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { formatCityDisplay } from '../utils/flightSearch';

// checkout page
const Checkout = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // State for upgrades
  const [selectedUpgrade, setSelectedUpgrade] = useState(null);
  const [isUpgraded, setIsUpgraded] = useState(false);
  
  // Get flight details from navigation state or URL parameters
  const navigationState = location.state;
  const selectedFlights = navigationState?.selectedFlights;
  const stateSearchParams = navigationState?.searchParams;
  
  const fromCode = stateSearchParams?.from || searchParams.get('from') || 'JFK';
  const toCode = stateSearchParams?.to || searchParams.get('to') || 'LAX';
  const departDate = stateSearchParams?.departDate || searchParams.get('departDate');
  const returnDate = stateSearchParams?.returnDate || searchParams.get('returnDate');
  const tripType = navigationState?.tripType || searchParams.get('tripType') || 'oneWay';
  const adults = stateSearchParams?.adults || parseInt(searchParams.get('adults')) || 1;
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Tuesday, August 26, 2025';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Extract flight data for display
  const getDepartFlightData = () => {
    if (tripType === 'roundTrip' && selectedFlights?.departFlight) {
      return {
        fareData: selectedFlights.departFlight.fareData,
        flightData: selectedFlights.departFlight.flightData
      };
    } else if (tripType === 'oneWay' && selectedFlights?.flightData) {
      return {
        fareData: selectedFlights.fareData,
        flightData: selectedFlights.flightData
      };
    }
    return null;
  };

  const getReturnFlightData = () => {
    if (tripType === 'roundTrip' && selectedFlights?.returnFlight) {
      return {
        fareData: selectedFlights.returnFlight.fareData,
        flightData: selectedFlights.returnFlight.flightData
      };
    }
    return null;
  };

  // Handle upgrade selection
  const handleUpgrade = (upgradeType) => {
    const upgradeOptions = {
      'main': {
        name: 'Main',
        additionalCost: 76,
        perPersonCost: 76,
        totalCost: 415.97,
        refundable: false
      },
      'main-plus': {
        name: 'Main Plus',
        additionalCost: 122,
        perPersonCost: 122,
        totalCost: 415.97,
        refundable: false
      },
      'main-select': {
        name: 'Main Select',
        additionalCost: 170,
        perPersonCost: 170,
        totalCost: 463.96,
        refundable: true
      },
      'first': {
        name: 'First',
        additionalCost: 398,
        perPersonCost: 398,
        totalCost: 1494.04,
        refundable: false
      }
    };

    // Use dynamic upgrade if available, otherwise use the upgradeType parameter
    let targetUpgrade = upgradeType;
    if (upgradeType === 'dynamic') {
      targetUpgrade = dynamicUpgrade.targetFare.toLowerCase().replace(' ', '-');
    }

    setSelectedUpgrade(upgradeOptions[targetUpgrade]);
    setIsUpgraded(true);
  };

  // Calculate pricing
  const calculateTotalPrice = () => {
    const departFlight = getDepartFlightData();
    const returnFlight = getReturnFlightData();
    
    if (!departFlight) return { total: 347.96, perPerson: 348 }; // Default values
    
    let totalPrice = 0;
    let isMiles = false;
    
    if (departFlight && departFlight.fareData.price) {
      const priceString = departFlight.fareData.price.toString();
      // Check if it's miles (contains "miles" or is a number with "K" for thousands)
      if (priceString.includes('miles') || priceString.includes('k miles') || priceString.includes('K miles')) {
        isMiles = true;
        const departPrice = parseFloat(priceString.replace(/[^0-9.-]+/g, "")) || 0;
        totalPrice += departPrice;
      } else {
        const departPrice = parseFloat(priceString.replace(/[^0-9.-]+/g, "")) || 0;
        totalPrice += departPrice;
      }
    }
    
    if (returnFlight && returnFlight.fareData.price) {
      const priceString = returnFlight.fareData.price.toString();
      const returnPrice = parseFloat(priceString.replace(/[^0-9.-]+/g, "")) || 0;
      totalPrice += returnPrice;
    }

    // Apply upgrade if selected
    if (selectedUpgrade) {
      totalPrice += selectedUpgrade.additionalCost;
    }
    
    return {
      total: selectedUpgrade ? selectedUpgrade.totalCost.toFixed(2) : (totalPrice * adults).toFixed(2),
      perPerson: selectedUpgrade ? selectedUpgrade.perPersonCost + Math.round(totalPrice / (tripType === 'roundTrip' ? 2 : 1)) : Math.round(totalPrice),
      isMiles: isMiles,
      upgrade: selectedUpgrade
    };
  };

  const departFlightData = getDepartFlightData();
  const returnFlightData = getReturnFlightData();
  const pricing = calculateTotalPrice();

  // Function to get dynamic upgrade options based on current fare
  const getDynamicUpgrade = () => {
    const currentFareType = departFlightData?.fareData?.type || 'Main';
    
    switch (currentFareType) {
      case 'Basic Economy':
        return {
          targetFare: 'Main',
          price: '+$76',
          totalPrice: '$415.97',
          benefits: [
            { type: 'fee', text: 'Pay to check bags' },
            { type: 'checkmark', text: 'Choose a Main Cabin seat' },
            { type: 'fee', text: 'Pay for an extra legroom seat' },
            { type: 'checkmark', text: 'Board in general group (6-8)' },
            { type: 'checkmark', text: 'Changes allowed' },
            { type: 'fee', text: 'Pay to choose another same-day flight' }
          ],
          benefitTitle: 'Main Cabin benefits'
        };
      case 'Main':
        return {
          targetFare: 'Main Plus',
          price: '+$122',
          totalPrice: '$415.97',
          benefits: [
            { type: 'checkmark', text: 'Check 1 bag free' },
            { type: 'checkmark', text: 'Choose a Main Cabin seat' },
            { type: 'checkmark', text: 'Choose an extra legroom seat' },
            { type: 'checkmark', text: 'Board earlier with Group 5' },
            { type: 'checkmark', text: 'Changes allowed' },
            { type: 'fee', text: 'Pay to choose another same-day flight' },
            { type: 'checkmark', text: 'Full refund to travel credit' }
          ],
          benefitTitle: 'More seat and bag benefits'
        };
      case 'Main Plus':
        return {
          targetFare: 'Main Select',
          price: '+$200',
          totalPrice: '$615.97',
          benefits: [
            { type: 'checkmark', text: 'Check 2 bags free' },
            { type: 'checkmark', text: 'Choose any Main Cabin seat' },
            { type: 'checkmark', text: 'Choose preferred or extra legroom seat' },
            { type: 'checkmark', text: 'Board earlier with Group 4' },
            { type: 'checkmark', text: 'Changes allowed' },
            { type: 'checkmark', text: 'Choose another same-day flight' },
            { type: 'checkmark', text: 'Full refund to travel credit' }
          ],
          benefitTitle: 'Premium cabin benefits'
        };
      default:
        // Default to Main Plus upgrade
        return {
          targetFare: 'Main Plus',
          price: '+$122',
          totalPrice: '$415.97',
          benefits: [
            { type: 'checkmark', text: 'Check 1 bag free' },
            { type: 'checkmark', text: 'Choose a Main Cabin seat' },
            { type: 'checkmark', text: 'Choose an extra legroom seat' },
            { type: 'checkmark', text: 'Board earlier with Group 5' },
            { type: 'checkmark', text: 'Changes allowed' },
            { type: 'fee', text: 'Pay to choose another same-day flight' },
            { type: 'checkmark', text: 'Full refund to travel credit' }
          ],
          benefitTitle: 'More seat and bag benefits'
        };
    }
  };

  const dynamicUpgrade = getDynamicUpgrade();

  // Helper function to render benefit icons
  const renderBenefitIcon = (type) => {
    if (type === 'checkmark') {
      return <span className="text-green-600 mr-2 text-sm">✓</span>;
    } else if (type === 'fee') {
      return <span className="text-blue-600 mr-2 text-sm">💳</span>;
    }
    return <span className="text-gray-600 mr-2 text-sm">•</span>;
  };

  return (
    <div className="min-h-screen bg-white">
      <main id="aa-content-frame">
        <div className="grid-container summary-div pad-bottom-lg max-w-[1240px] mx-auto px-4 py-6">
          {/* Header */}
          <div className="header-container pad-bottom-lg">
            <div className="grid-x desktop-header flex justify-between items-center mb-8">
              <div className="cell">
                <h1 className="text-[44px] mb-4 font-bold leading-[48px] text-[#181e25]" style={{fontFamily: 'AmericanSans Var, Arial, sans-serif'}}>
                  <span aria-hidden="true">Choose flights</span>
                  <span aria-live="polite" className="sr-only">Your trip summary page loaded</span>
                </h1>
              </div>
              <div className="cell">
                <a 
                  id="new-search-desktop" 
                  className="new-search-link text-blue-600 hover:underline" 
                  href="/"
                >
                  « <span className="hidden">Start a </span> New search
                </a>
              </div>
            </div>
          </div>

          {/* Trip Summary Header */}
          <div className="grid-x desktop-only mb-6">
            <div className="cell">
              <h2 id="tripSummaryHeader" className="your-trip-summary text-[2rem] font-normal leading-[1.2] text-[#00467f] mt-[25px]" style={{fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif'}}>
                Your trip summary
              </h2>
            </div>
          </div>

          {/* Main Content */}
          <div className="default-container summary-info">
            <div className="inner-container">
              {/* Main Card with Left Pricing and Right Flight Details */}
              <div className="bg-white border border-gray-300 rounded-[4px] shadow-lg overflow-hidden">
                <div className="flex">
                  
                  {/* Left Side - Cost Summary and Refundable Option */}
                  <div className="w-1/3 bg-gray-100 p-6">
                    {/* Main Pricing Section */}
                    <div className="main-pricing mb-6">
                      <h3 className="cabin-header" style={{fontFamily: 'AmericanSans Var, Arial, sans-serif', fontSize: '24px', fontWeight: '500', color: '#000', marginBottom: '8px'}}>
                        {selectedUpgrade ? selectedUpgrade.name : (departFlightData?.fareData?.type || 'Main')}
                      </h3>
                      
                      <div className="trip-type mb-4">
                        <span style={{fontSize: '14px', color: '#666'}}>
                          {tripType === 'roundTrip' ? 'Round trip' : 'One way'} ({selectedUpgrade?.refundable ? 'refundable' : 'non-refundable'})
                        </span>
                      </div>
                      
                      <div className="price-display">
                        <div className="price-info flex items-center">
                          {!pricing.isMiles && <span className="symbol text-6xl font-light" style={{color: '#000'}}>$</span>}
                          <span className="amount text-6xl font-light" style={{color: '#000'}}>{pricing.perPerson}</span>
                          {pricing.isMiles && <span className="miles-label text-3xl font-light ml-2" style={{color: '#000'}}>miles</span>}
                          <div className="per-person ml-2">
                            <span style={{fontSize: '18px', color: '#000', lineHeight: '1.2'}}>per <br />person</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="pricing-details">
                        <div className="total " style={{ fontSize: '14px', color: '#666'}}>
                          <span id="total-text">Total </span>
                          <span id="total-amount">{pricing.isMiles ? pricing.total + ' miles' : '$' + pricing.total}</span>
                          <span> ({adults > 1 ? 'all passengers' : '1 passenger'})</span>
                        </div>
                        
                        <div className="taxes-and-fees " style={{ fontSize: '14px', color: '#666'}}>
                          Includes taxes and carrier-imposed fees
                        </div>
                        
                        <div className="tax-info-link">
                          <a href="javascript:void(0);" id="tax-link" className="text-blue-800 hover:underline" style={{fontSize: '14px'}}>
                            Price and tax information <span aria-hidden="true" className="icon-newpage">↗</span>
                          </a>
                        </div>
                        
                        <div className="fees-link mb-4">
                          <a target="_blank" id="bag-fees-link" href="/i18n/customer-service/support/optional-service-fees.jsp" className="text-blue-600 hover:underline" style={{fontSize: '14px'}}>
                            Bag and optional fees <span className="show-for-sr">, Opens in a new window.</span><span className="icon-newpage" aria-hidden="true">↗</span>
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Refundable Option Section */}
                    <div className="refundable-section">
                      <h4 className="refundable-title " style={{fontSize: '18px', fontWeight: 'bold', color: '#000'}}>
                        Make my trip refundable
                      </h4>
                      
                      <div className="refundable-benefits mb-3" style={{fontSize: '15px', color: '#666'}}>
                        No change fees; cancel any time 
                      </div>
                      
                      <div className="refundable-pricing mb-4">
                        <div className="trip-label" style={{fontSize: '15px', color: '#666', marginBottom: '8px', display: 'block'}}>
                          Round trip
                        </div>
                        
                        <div className="additional-cost flex items-start ">
                          <span aria-hidden="true" className="sign text-2xl font-light" style={{color: '#000'}}>+</span>
                          <span className="hidden-accessible">Additional</span>
                          <span className="symbol text-3xl font-light" style={{color: '#000'}}>$</span>
                          <span className="amount text-3xl font-light" style={{color: '#000'}}>131</span>
                          <div className="per-person ml-2">
                            <span style={{fontSize: '12px', color: '#000', lineHeight: '1.1'}}>per <br />person</span>
                          </div>
                        </div>
                  
                      </div>
                      
                      <button type="button" className="refundable-button  py-3 px-10 border-2 border-blue-400 text-blue-800 rounded text-sm font-medium hover:bg-blue-50">
                        Make it refundable
                      </button>
                    </div>
                  </div>

                  {/* Right Side - Flight Details */}
                  <div className="flex-1 p-6">
                    
                    {/* DEPART Flight */}
                    <div className="mb-6">
                      <div className="mb-1">
                        <span className="text-green-600 font-bold text-sm">DEPART</span>
                      </div>
                      <h2 className="text-xl text-blue-200 font-normal mb-1" style={{
                        display: 'block',
                        // fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                        fontSize: '24px',
                        color: '#00467f'
                      }}>
                        {formatCityDisplay(fromCode)} to {formatCityDisplay(toCode)}
                      </h2>
                      <h3 className="text-base text-gray-600 mb-4" style={{
                        display: 'block',
                        // fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                        fontSize: '21px',
                        color: '#627a88'
                      }}>
                        {formatDate(departDate)}
                      </h3>

                      <div className="flex items-center justify-between mb-3">
                        {/* Flight Times */}
                        <div className="flex items-center">
                          <div className="text-center mr-4">
                            <div className="text-gray-600 text-sm">{departFlightData?.flightData?.departureCode || fromCode}</div>
                            <div className="text-xl font-medium">{departFlightData?.flightData?.departureTime || '7:00 AM'}</div>
                          </div>
                          <div className="mx-4 text-gray-400">→</div>
                          <div className="text-center ml-4">
                            <div className="text-gray-600 text-sm">{departFlightData?.flightData?.arrivalCode || toCode}</div>
                            <div className="text-xl font-medium">{departFlightData?.flightData?.arrivalTime || '8:30 AM'}</div>
                          </div>
                        </div>

                        {/* Duration */}
                        <div className="text-center px-3">
                          <div className="font-medium text-sm">{departFlightData?.flightData?.duration || '1h 30m'}</div>
                        </div>

                        {/* Stops */}
                        <div className="text-center px-3">
                          <div className="font-medium text-sm">{departFlightData?.flightData?.stops || 'Nonstop'}</div>
                        </div>

                        {/* Cabin Type */}
                        <div className="text-center px-3">
                          <div className="font-medium text-sm">{selectedUpgrade ? selectedUpgrade.name : (departFlightData?.fareData?.type || 'Main')}</div>
                        </div>
                      </div>

                      {/* Aircraft Info */}
                      <div className="text-sm text-gray-600 mb-3">
                        {departFlightData?.flightData?.segments?.[0]?.flight || 'AA1097'} • {departFlightData?.flightData?.segments?.[0]?.aircraft || '738-Boeing 737'}
                        {departFlightData?.flightData?.segments?.[0]?.operator && (
                          <span> • {departFlightData.flightData.segments[0].operator}</span>
                        )}
                      </div>

                      {/* Action Links */}
                      <div className="flex items-center space-x-3 mb-4">
                        <button className="text-blue-600 hover:underline text-sm">
                          Details
                        </button>
                        <span className="text-gray-400">|</span>
                        <button className="text-blue-600 hover:underline text-sm">
                          Change
                        </button>
                      </div>
                    </div>

                    {/* RETURN Flight - Only show for round trip */}
                    {tripType === 'roundTrip' && returnFlightData && (
                      <div className="border-t border-gray-200 pt-6">
                        <div className="mb-2">
                          <span className="text-green-700 font-bold text-sm">RETURN</span>
                        </div>
                        <h2 className="text-xl text-blue-200 font-normal mb-1" style={{
                          display: 'block',
                          // fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                          fontSize: '24px',
                          color: '#00467f'
                        }}>
                          {formatCityDisplay(toCode)} to {formatCityDisplay(fromCode)}
                        </h2>
                        <h3 className="text-base text-gray-600 mb-4" style={{
                          display: 'block',
                          // fontFamily: 'AmericanSansLight, Helvetica, Arial, sans-serif',
                          fontSize: '21px',
                          color: '#627a88'
                        }}>
                          {formatDate(returnDate)}
                        </h3>

                        <div className="flex items-center justify-between mb-3">
                          {/* Flight Times */}
                          <div className="flex items-center">
                            <div className="text-center mr-4">
                              <div className="text-gray-600 text-sm">{returnFlightData?.flightData?.departureCode || toCode}</div>
                              <div className="text-xl font-medium">{returnFlightData?.flightData?.departureTime || '7:12 AM'}</div>
                            </div>
                            <div className="mx-4 text-gray-400">→</div>
                            <div className="text-center ml-4">
                              <div className="text-gray-600 text-sm">{returnFlightData?.flightData?.arrivalCode || fromCode}</div>
                              <div className="text-xl font-medium">{returnFlightData?.flightData?.arrivalTime || '8:42 AM'}</div>
                            </div>
                          </div>

                          {/* Duration */}
                          <div className="text-center px-3">
                            <div className="font-medium text-sm">{returnFlightData?.flightData?.duration || '1h 30m'}</div>
                          </div>

                          {/* Stops */}
                          <div className="text-center px-3">
                            <div className="font-medium text-sm">{returnFlightData?.flightData?.stops || 'Nonstop'}</div>
                          </div>

                          {/* Cabin Type */}
                          <div className="text-center px-3">
                            <div className="font-medium text-sm">{selectedUpgrade ? selectedUpgrade.name : (returnFlightData?.fareData?.type || 'Main')}</div>
                          </div>
                        </div>

                        {/* Aircraft Info */}
                        <div className="text-sm text-gray-600 mb-3">
                          {returnFlightData?.flightData?.segments?.[0]?.flight || 'AA5009'} • {returnFlightData?.flightData?.segments?.[0]?.aircraft || 'E75-Embraer 175'}
                          {returnFlightData?.flightData?.segments?.[0]?.operator && (
                            <span> • {returnFlightData.flightData.segments[0].operator}</span>
                          )}
                        </div>

                        {/* Action Links */}
                        <div className="flex items-center space-x-3">
                          <button className="text-blue-600 hover:underline text-sm">
                            Details
                          </button>
                          <span className="text-gray-400">|</span>
                          <button className="text-blue-600 hover:underline text-sm">
                            Change
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade Options Section - Hide when upgrade is selected */}
          {!isUpgraded && (
            <div className="mt-8">
              <div className="bg-white rounded-lg overflow-hidden shadow-sm">
              
              {/* Upgrade to Main Plus */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  
                  {/* Left Side - Upgrade Details */}
                  <div className="flex-1 mr-6">
                    <div className="flex items-center mb-4">
                      <h2 className="text-2xl text-blue-600 font-normal mr-2">Upgrade to {dynamicUpgrade.targetFare}</h2>
                      <div className="w-5 h-5 rounded-full border-2 border-blue-600 flex items-center justify-center">
                        <span className="text-blue-600 text-xs">i</span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">Round trip (non-refundable)</div>
                      <div className="flex items-start mb-1">
                        <span className="text-blue-600 text-3xl font-light">{dynamicUpgrade.price}</span>
                        <span className="text-sm text-gray-600 ml-2">per<br/>person</span>
                      </div>
                      <div className="text-sm text-gray-600">Total {dynamicUpgrade.totalPrice} (all passengers)</div>
                    </div>
                    
                    <button 
                      className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded hover:bg-blue-50 text-sm font-medium"
                      onClick={() => handleUpgrade('dynamic')}
                    >
                      Upgrade
                    </button>
                  </div>

                  {/* Middle - Benefits */}
                  <div className="flex-1 mr-6">
                    <h3 className="text-lg text-blue-600 font-medium mb-4">{dynamicUpgrade.benefitTitle}</h3>
                    
                    <div className="space-y-2">
                      {dynamicUpgrade.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-start">
                          {renderBenefitIcon(benefit.type)}
                          <span className="text-sm text-gray-700">{benefit.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Side - Image */}
                  <div className="w-80">
                    <img 
                      src="./Upsell-MainCabinPlus.jpg" 
                      alt="Main Plus Experience" 
                      className="w-full h-auto rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-80 h-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">Main Plus Image</div>';
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Gray Divider */}
              <div className="border-t border-gray-300"></div>

              {/* Upgrade to Main Select */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  
                  {/* Left Side - Upgrade Details */}
                  <div className="flex-1 mr-6">
                    <div className="flex items-center mb-4">
                      <h2 className="text-2xl text-blue-600 font-normal mr-2">Upgrade to Main Select</h2>
                      <div className="w-5 h-5 rounded-full border-2 border-blue-600 flex items-center justify-center">
                        <span className="text-blue-600 text-xs">i</span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">Round trip (refundable)</div>
                      <div className="flex items-start mb-1">
                        <span className="text-blue-600 text-3xl font-light">+$170</span>
                        <span className="text-sm text-gray-600 ml-2">per<br/>person</span>
                      </div>
                      <div className="text-sm text-gray-600">Total $463.96 (all passengers)</div>
                    </div>
                    
                    <button 
                      className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded hover:bg-blue-50 text-sm font-medium"
                      onClick={() => handleUpgrade('main-select')}
                    >
                      Upgrade
                    </button>
                  </div>

                  {/* Middle - Benefits */}
                  <div className="flex-1 mr-6">
                    <h3 className="text-lg text-blue-600 font-medium mb-4">Refundable with more benefits</h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <span className="text-blue-600 mr-2 text-sm">💳</span>
                        <span className="text-sm text-gray-700">Pay to check bags</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Choose a Main Cabin seat</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Choose an extra legroom seat</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Priority check-in and boarding access (Group 4)</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Changes allowed</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Choose another same-day flight</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Full refund to original form of payment</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Image */}
                  <div className="w-80">
                    <img 
                      src="./Upsell-MainCabinSelect.jpg" 
                      alt="Main Select Experience" 
                      className="w-full h-auto rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-80 h-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">Main Select Image</div>';
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Gray Divider */}
              <div className="border-t border-gray-300"></div>

              {/* Upgrade to First */}
              <div className="p-6">
                <div className="flex items-start justify-between">
                  
                  {/* Left Side - Upgrade Details */}
                  <div className="flex-1 mr-6">
                    <div className="flex items-center mb-4">
                      <h2 className="text-2xl text-aa-dark-blue font-normal mr-2">Upgrade to First</h2>
                      <div className="w-5 h-5 rounded-full border-2 border-blue-600 flex items-center justify-center">
                        <span className="text-blue-600 text-xs">i</span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 mb-2">Round trip (non-refundable)</div>
                      <div className="flex items-start mb-1">
                        <span className="text-aa-dark-blue text-3xl font-light">+$398</span>
                        <span className="text-sm text-gray-600 ml-2">per<br/>person</span>
                      </div>
                      <div className="text-sm text-gray-600">Total $1,494.04 (all passengers)</div>
                    </div>
                    
                    <button 
                      className="px-6 py-2 border-2 border-aa-dark-blue text-aa-dark-blue rounded hover:bg-blue-50 text-sm font-medium"
                      onClick={() => handleUpgrade('first')}
                    >
                      Upgrade
                    </button>
                  </div>

                  {/* Middle - Benefits */}
                  <div className="flex-1 mr-6">
                    <h3 className="text-lg text-aa-dark-blue font-medium mb-4">Best way to travel</h3>
                    
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Check 2 bags free</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Choose our largest, most comfortable seat</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Priority check-in and boarding access</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Changes allowed</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Choose another same-day flight</span>
                      </div>
                      <div className="flex items-start">
                        <span className="text-green-600 mr-2 text-sm">✓</span>
                        <span className="text-sm text-gray-700">Full refund to travel credit</span>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Image */}
                  <div className="w-80">
                    <img 
                      src="./Upsell-First.jpg" 
                      alt="First Class Experience" 
                      className="w-full h-auto rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-80 h-48 bg-gray-200 rounded-lg flex items-center justify-center text-gray-500">First Class Image</div>';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Citi Card Offer */}
          <div className="mt-8">
            <div id="promotion-spot" className="w-full">
              <div className="bg-gray-100 border border-gray-300 rounded-lg overflow-hidden">
                <div className="flex pt-2 pb-8 px-2 items-center gap-2">
                  
                  {/* Left Side - Card Image */}
                  <div className="w-1/4 flex items-center justify-center">
                    <img 
                      src="/hero-card-platinum.png" 
                      alt="Citi AAdvantage Card" 
                      className="w-80 h-auto transform -rotate-12"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="w-90 h-80 bg-gray-400 rounded-lg flex items-center justify-center text-gray-600 transform -rotate-12">Citi Card</div>';
                      }}
                    />
                  </div>

                  {/* Right Side - Content Card */}
                  <div className="flex-1">
                  <div className="bg-red-600 text-white px-3 py-1 text-xs font-bold rounded mb-3 inline-block">
                            NEW CARDMEMBER OFFER
                          </div>
                    <div className="bg-white border border-gray-300 rounded-lg p-2 shadow-sm">
                      <div className="flex gap-10">
                        
                        {/* Left Content Area */}
                        <div>                        
                          <div >
                            <span className="text-sm text-gray-600">Earn up to a</span>
                          </div>           
                          <div className="flex items-baseline mb-1">
                            <span className="text-4xl font-bold text-black">$200</span>
                            <span className="text-5xl mx-4 text-black">+</span>
                            <span className="text-4xl font-bold text-black">40,000</span>
                          </div>
                          
                          <div className="flex text-sm text-gray-700 mb-2">
                            <span className="mr-4">statement credit</span>
                            <span>bonus miles</span>
                          </div>    
                        </div>

                        {/* Right Payment Summary */}
                        <div className="flex-1">
                          <div className="flex justify-between gap-12">
                            <span className="text-sm text-gray-700">Pay today:</span>
                            <span className=" text-gray-600">$280.48</span>
                          </div>
                          <div className="flex justify-between gap-12">
                            <span className="text-sm text-gray-700">Card statement credit:</span>
                            <span className=" text-gray-600">-$200.00</span>
                          </div>
                          <div className="border-t border-gray-400 pt-2">
                            <div className="flex justify-between gap-12">
                              <span className="text-sm text-gray-700">Total after statement credit:</span>
                              <span className=" text-gray-600">$80.48</span>
                            </div>
                          </div>
                        </div>
                        
                      </div>
                      
                    </div>
                    <p className="text-sm text-gray-700 mb-4 max-w-md leading-relaxed">
                            Plus, get your first checked bag free on domestic American Airlines itineraries. Terms apply.
                          </p>
                          
                          <button className="px-6 py-2 border-2 border-blue-600 text-blue-600 rounded hover:bg-blue-50 text-sm font-medium">
                            Learn more ↗
                          </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid-x btn-container mt-8">
            <div className="cell large-3 large-offset-6 w-full lg:w-1/4 lg:ml-auto mr-4">
              <button 
                id="login-continue-btn" 
                className="button continue-btn secondary w-full py-3 px-6 border-2 border-blue-600 text-blue-600 bg-white rounded hover:bg-blue-50 font-medium"
              >
                Log in and continue
              </button>
            </div>
            <div className="cell large-3 text-right w-full lg:w-1/4">
              <button 
                id="continue-as-guest-btn" 
                className="button primary continue-btn w-full py-3 px-6 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
              >
                Continue as guest
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Checkout;